import React, { useState, useEffect } from "react";
import { Box, Typography, Chip, IconButton } from "@mui/material";
import { useLocation } from "react-router-dom";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import useSoundFx from "../../hooks/useSoundFx";
import { useSoundContext } from "../../context/SoundContext";
import { useThemeContext } from "../../context/ThemeContext";

const navItems = [
  { label: "Intro", id: "hero" },
  { label: "About", id: "about" },
  { label: "Projects", id: "projects" },
  { label: "Experience", id: "experience" },
];

const Header = () => {
  const [activeSection, setActiveSection] = useState("hero");
  const { playClick, playMuteToggle } = useSoundFx();
  const { isMuted, toggleMute } = useSoundContext();
  const { isDark, toggleTheme } = useThemeContext();
  const location = useLocation();
  const isDetailPage = location.pathname.startsWith("/project/");

  useEffect(() => {
    const observers = navItems.map(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { threshold: 0.3 }
      );
      observer.observe(el);
      return observer;
    });
    return () => observers.forEach((obs) => obs?.disconnect());
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const navColor = isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.65)";
  const iconColor = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)";
  const iconHover = isDark ? "white" : "#0d1b2a";

  return (
    <Box
      component="header"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        px: { xs: 2.5, md: 6 },
        py: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: isDark ? "rgba(10,20,35,0.75)" : "rgba(240,244,248,0.85)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        boxShadow: "0 1px 24px rgba(0,0,0,0.12)",
        borderBottom: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.06)",
        transition: "background-color 0.3s ease, border-color 0.3s ease",
      }}
    >
      {/* Logo */}
      <Typography
        onClick={() => { playClick(); scrollTo("hero"); }}
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 700,
          fontSize: "1.1rem",
          color: isDark ? "white" : "#0d1b2a",
          cursor: "pointer",
          letterSpacing: 0.5,
          userSelect: "none",
          transition: "color 0.3s",
          flexShrink: 0,
        }}
      >
        faza<Box component="span" sx={{ color: "#00e676" }}>.</Box>
      </Typography>

      {/* Nav links — hidden on mobile and on detail pages */}
      <Box
        component="nav"
        sx={{ display: { xs: "none", md: isDetailPage ? "none" : "flex" }, gap: 4, alignItems: "center" }}
      >
        {navItems.map(({ label, id }) => {
          const isActive = activeSection === id;
          const handleActivate = () => { playClick(); scrollTo(id); };
          return (
            <Typography
              key={id}
              role="button"
              tabIndex={0}
              aria-current={isActive ? "true" : undefined}
              onClick={handleActivate}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleActivate();
                }
              }}
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.95rem",
                fontWeight: isActive ? 600 : 400,
                color: isActive ? "#00e676" : navColor,
                cursor: "pointer",
                userSelect: "none",
                transition: "color 0.2s",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -4,
                  left: 0,
                  width: isActive ? "100%" : "0%",
                  height: "2px",
                  backgroundColor: "#00e676",
                  transition: "width 0.2s",
                },
                "&:hover": { color: isDark ? "white" : "#0d1b2a" },
              }}
            >
              {label}
            </Typography>
          );
        })}
      </Box>

      {/* Right controls — always visible */}
      <Box sx={{ display: "flex", gap: { xs: 0.5, md: 1 }, alignItems: "center" }}>
        {/* Mute toggle */}
        <IconButton
          onClick={() => { playMuteToggle(!isMuted); toggleMute(); }}
          aria-label={isMuted ? "Unmute sound" : "Mute sound"}
          size="small"
          sx={{
            color: isMuted
              ? isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)"
              : iconColor,
            transition: "color 0.2s",
            "&:hover": { color: iconHover },
          }}
        >
          {isMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
        </IconButton>

        {/* Theme toggle */}
        <IconButton
          onClick={() => { playClick(); toggleTheme(); }}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          size="small"
          sx={{
            color: iconColor,
            transition: "color 0.2s",
            "&:hover": { color: iconHover },
          }}
        >
          {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
        </IconButton>

        {/* Resume */}
        <Chip
          component="a"
          href="/resume.pdf"
          download="Muhammad_Faza_Hardiansyah_Resume.pdf"
          label="Resume"
          onClick={playClick}
          clickable
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: { xs: "0.78rem", md: "0.85rem" },
            fontWeight: 600,
            color: "#00e676",
            backgroundColor: "rgba(0,230,118,0.1)",
            border: "1px solid rgba(0,230,118,0.35)",
            height: { xs: 28, md: 32 },
            "&:hover": { backgroundColor: "rgba(0,230,118,0.2)" },
          }}
        />
      </Box>
    </Box>
  );
};

export default Header;
